import React from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import BetaProductCard from 'components/upb_viewer_beta/BetaProductCard';
import {
  FG_3,
  FONT_BODY,
  FONT_SIZE_BODY,
  RADIUS_LG,
  SKELETON_BG,
} from 'components/upb_viewer_beta/css/betaTokens';

/**
 * BetaProductGrid — responsive grid of BetaProductCards.
 *
 * Props:
 *   products  Array<object>
 *   loading   boolean — show skeleton tiles
 *   onProductClick (product) => void
 */
function BetaProductGrid({ products, loading, onProductClick }) {
  if (loading) {
    return (
      <div className={css(styles.grid)}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
          <div key={n} className={css(styles.skeleton)} />
        ))}
      </div>
    );
  }

  if (!products || products.length === 0) {
    return <div className={css(styles.empty)}>No products found.</div>;
  }

  return (
    <div className={css(styles.grid)}>
      {products.map(p => (
        <BetaProductCard key={p.id || p.fsa_id} product={p} onClick={onProductClick} />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  grid: {
    display: 'grid',
    // Fixed 5 columns (not auto-fill): the page size is 50, so 5 columns yields
    // exactly 10 complete rows with no orphaned trailing card. Auto-fill gave a
    // variable column count on wide monitors (e.g. 7), which left 50 % cols
    // leftover cards on the last row. 5 also matches the category page's
    // card density. minmax(0, 1fr) lets cards shrink on narrow screens
    // without overflowing.
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: 14,
  },

  skeleton: {
    aspectRatio: '3 / 4',
    borderRadius: RADIUS_LG,
    backgroundColor: SKELETON_BG,
    animationName: {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.4 },
      '100%': { opacity: 1 },
    },
    animationDuration: '1.4s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },

  empty: {
    padding: 32,
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_BODY,
    color: FG_3,
    textAlign: 'center',
  },
});

BetaProductGrid.propTypes = {
  products: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  onProductClick: PropTypes.func,
};

BetaProductGrid.defaultProps = {
  products: [],
  loading: false,
  onProductClick: null,
};

export default BetaProductGrid;
